import React, { useState } from "react";
import styled from "styled-components";
import {
  X,
  ChevronUp,
  ChevronDown,
  Eye,
  RotateCcw,
  CreditCard,
  CaseSensitive as University,
  DollarSign,
} from "lucide-react";
import { DonationDetailModal } from "./DonationDetailModal";
import { RefundModal } from "./RefundModal";
import {
  FundraisersDonationsBoardResponse,
  PromoterProgressModel,
} from "../models";

const SectionHeaderWithSearch = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const SectionTitleWithIcon = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 300px;

  @media (max-width: 640px) {
    width: 250px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 16px center;
  background-size: 20px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const DataTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCardContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const MobileDonationCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const MobileDonorName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
`;

const MobileDonorColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const MobileAmount = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #10b981;
  text-align: right;
  line-height: 1.3;
`;

const MobileContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 5px;
`;

const MobileContactItem = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const MobileRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
`;

const MobileDateOnly = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  margin-top: 5px;
  text-align: right;
`;

const MobileTimeOnly = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  text-align: right;
  margin-top: 4px;
`;

const TableHeader = styled.div<{ columns: string }>`
  display: grid;
  grid-template-columns: 2.5fr 1.2fr 2fr 2fr;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #10b981;
  }
`;

const SortIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.6;
`;

const TableRow = styled.div<{ columns: string }>`
  display: grid;
  grid-template-columns: 2.5fr 1.2fr 2fr 2fr;
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  align-items: center;

  &:hover {
    background: linear-gradient(135deg, #f8fafc, #ffffff);
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const DonorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DonorAvatar = styled.div<{ isAnonymous?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: ${(props) => (props.isAnonymous ? "none" : "flex")};
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const DonorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DonorName = styled.div<{ isAnonymous?: boolean }>`
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  margin-bottom: 4px;
  margin-left: ${(props) => (props.isAnonymous ? "64px" : "0")};

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ContactItem = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const AmountValue = styled.div`
  font-weight: 800;
  color: #10b981;
  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const DateTimeValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: normal;

  @media (max-width: 768px) {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
  }
`;

const PaymentMethodContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const PaymentIcon = styled.div<{ cardType?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;

  ${(props) => {
    switch (props.cardType) {
      case "visa":
        return "background: linear-gradient(135deg, #1A1F71, #0F3460);";
      case "mastercard":
        return "background: linear-gradient(135deg, #EB001B, #F79E1B);";
      case "amex":
        return "background: linear-gradient(135deg, #006FCF, #00A19C);";
      case "discover":
        return "background: linear-gradient(135deg, #FF6000, #F16000);";
      default:
        return "background: linear-gradient(135deg, #64748b, #475569);";
    }
  }}
`;

const PaymentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
`;

const PaymentType = styled.div`
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
`;

const PaymentAccount = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const CommentContainer = styled.div`
  position: relative;
`;

const CommentText = styled.div<{ hasTooltip: boolean }>`
  font-size: 14px;
  color: #1e293b;
  font-weight: normal;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: ${(props) => (props.hasTooltip ? "help" : "default")};
`;

const CommentTooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  white-space: pre-line;
  text-align: center;
  max-width: 300px;
  min-width: 120px;
  z-index: 1000;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.2s ease;
  pointer-events: none;
  margin-bottom: 8px;
  line-height: 1.4;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const ActionIcons = styled.div`
  display: flex;
  gap: 8px;
`;

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Tooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: #ffffff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.2s ease;
  pointer-events: none;
  margin-bottom: 8px;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: translateY(-1px);
  }
`;

const NoResults = styled.div`
  padding: 40px;
  text-align: center;
  color: #64748b;
  font-size: 16px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const StatTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
`;

const StatChange = styled.div<{ positive: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.positive ? "#10b981" : "#ef4444")};
`;

type SortField = "name" | "donation" | "date" | null;
type SortDirection = "asc" | "desc";

interface PromoterDonationsTableProps {
  promoterId: string;
  fundraiserId: string;
  hideActions?: boolean;
  isPromoterDashboard?: boolean;
  donations: FundraisersDonationsBoardResponse[];
  promoter: PromoterProgressModel;
}

export const PromoterDonationsTable: React.FC<PromoterDonationsTableProps> = ({
  promoterId,
  donations,
  promoter,
  fundraiserId,
  hideActions = false,
  isPromoterDashboard = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDonation, setSelectedDonation] =
    useState<FundraisersDonationsBoardResponse | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundDonation, setRefundDonation] =
    useState<FundraisersDonationsBoardResponse | null>(null);
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [hoveredComment, setHoveredComment] = useState<string | null>(null);

  // Get promoter-specific donations
  //const donations =  fundraiserStore.getPromoterDonations(promoterId, fundraiserId);
  //const promoter = fundraiserStore.getPromoterById(promoterId);

  const formatCurrency = (amount: number) => {
    // Cents only when there are any: a $0.25 gift used to show as "$0" and $121.45 as "$121".
    const hasCents = Math.round(amount * 100) % 100 !== 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const formatDateOnly = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const formatTimeOnly = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const getInitials = (name: string) => {
    if (!name || name === "Anonymous Donor") {
      return "AD";
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getDisplayName = (donation: FundraisersDonationsBoardResponse) => {
    // Show "Anonymous Donation" if donation is anonymous
    if (donation.isAnonymous) {
      return "Anonymous Donation";
    }

    // Always show actual name in admin views - use firstName + lastName if available
    if (donation.firstName && donation.lastName) {
      return `${donation.firstName} ${donation.lastName}`;
    }
    return donation.displayName || "Unknown Donor";
  };

  const getContactInfo = (donation: FundraisersDonationsBoardResponse) => {
    // Always show contact info in admin views
    const contacts: string[] = [];
    if (donation.firstName) contacts.push(donation.firstName);
    if (donation.lastName) contacts.push(donation.lastName);
    // if (donation.donorEmail) contacts.push(donation.donorEmail);
    // if (donation.donorMobile) contacts.push(donation.donorMobile);
    return contacts;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "date" ? "desc" : "asc");
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const handleViewDetails = (donation: FundraisersDonationsBoardResponse) => {
    setSelectedDonation(donation);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedDonation(null);
  };

  const handleRefund = (donation: FundraisersDonationsBoardResponse) => {
    setRefundDonation(donation);
    setShowRefundModal(true);
  };

  const handleCloseRefundModal = () => {
    setShowRefundModal(false);
    setRefundDonation(null);
  };

  const filteredDonations = donations.filter((donation) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      donation.displayName.toLowerCase().includes(searchLower) ||
      (donation.firstName &&
        donation.firstName.toLowerCase().includes(searchLower)) ||
      (donation.lastName &&
        donation.lastName.toLowerCase().includes(searchLower)) ||
      (donation.comment && donation.comment.toLowerCase().includes(searchLower))
    );
  });

  const getSortedDonations = () => {
    let sorted = [...filteredDonations];

    if (sortField === "name") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.displayName
          .toLowerCase()
          .localeCompare(b.displayName.toLowerCase());
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "donation") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.amount - b.amount;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "date") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.donatedOn.getTime() - b.donatedOn.getTime();
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return sorted;
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <SortIcon>
          <ChevronUp size={12} />
          <ChevronDown size={12} style={{ marginTop: "-2px" }} />
        </SortIcon>
      );
    }

    return (
      <SortIcon style={{ opacity: 1 }}>
        {sortDirection === "asc" ? (
          <ChevronUp size={16} />
        ) : (
          <ChevronDown size={16} />
        )}
      </SortIcon>
    );
  };

  // const renderPaymentMethod = (paymentMethod: FundraisersDonationsBoardResponse['paymentMethod']) => {
  //   if (paymentMethod.type === 'card') {
  //     const cardName = paymentMethod.brand?.toUpperCase() || 'CARD';
  //     return (
  //       <PaymentMethodContainer>
  //         <PaymentIcon cardType={paymentMethod.brand}>
  //           <CreditCard size={16} />
  //         </PaymentIcon>
  //         <PaymentDetails>
  //           <PaymentType>{cardName}</PaymentType>
  //           <PaymentAccount>•••• {paymentMethod.last4}</PaymentAccount>
  //         </PaymentDetails>
  //       </PaymentMethodContainer>
  //     );
  //   } else {
  //     return (
  //       <PaymentMethodContainer>
  //         <PaymentIcon>
  //           <University size={16} />
  //         </PaymentIcon>
  //         <PaymentDetails>
  //           <PaymentType>ACH</PaymentType>
  //           <PaymentAccount>••••••{paymentMethod.last6}</PaymentAccount>
  //         </PaymentDetails>
  //       </PaymentMethodContainer>
  //     );
  //   }
  // };

  // Check if comment is too long (would be truncated)
  const isCommentTruncated = (comment: string) => {
    if (!comment) return false;
    // Rough estimation: if comment is longer than ~150 characters or has line breaks, it might be truncated
    return comment.length > 150 || comment.includes("\n");
  };

  const renderCommentCell = (donation: FundraisersDonationsBoardResponse) => {
    if (!donation.comment || donation.comment.trim() === "") {
      return <div></div>;
    }

    const isTruncated = isCommentTruncated(donation.comment);
    const isHovered = hoveredComment === donation.donationId;

    return (
      <CommentContainer>
        <CommentText
          hasTooltip={isTruncated}
          onMouseEnter={() =>
            isTruncated && setHoveredComment(donation.donationId)
          }
          onMouseLeave={() => setHoveredComment(null)}
        >
          {donation.comment}
        </CommentText>
        {isTruncated && (
          <CommentTooltip visible={isHovered}>
            {donation.comment}
          </CommentTooltip>
        )}
      </CommentContainer>
    );
  };

  const sortedDonations = getSortedDonations();

  // Calculate stats for the promoter
  const totalRaised = donations.reduce((sum, d) => sum + d.amount, 0);
  const donationCount = donations.length;
  const averageDonation = donationCount > 0 ? totalRaised / donationCount : 0;
  const goal = promoter?.goalAmount || 25000;
  const progressPercentage = (totalRaised / goal) * 100;

  return (
    <>
      <SectionHeaderWithSearch>
        {isPromoterDashboard ? (
          <SectionTitleWithIcon>
            <DollarSign size={20} color="#3b82f6" />
            Donations ({filteredDonations.length})
          </SectionTitleWithIcon>
        ) : (
          <SectionTitle>Donations ({filteredDonations.length})</SectionTitle>
        )}
        <SearchWrapper>
          <SearchInput
            placeholder="Search donations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton onClick={clearSearch} title="Clear search">
              <X size={16} />
            </ClearButton>
          )}
        </SearchWrapper>
      </SectionHeaderWithSearch>

      {/* Mobile Card Layout */}
      <MobileCardContainer>
        {sortedDonations.length === 0 ? (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#64748b",
              fontSize: "16px",
            }}
          >
            {searchTerm
              ? `No donations found matching "${searchTerm}"`
              : "No donations yet"}
          </div>
        ) : (
          sortedDonations.map((donation, key) => (
            <MobileDonationCard key={key}>
              <MobileCardHeader>
                <MobileDonorColumn>
                  <MobileDonorName>
                    {donation.isAnonymous
                      ? "Anonymous Donor"
                      : donation.getDisplayName()}
                  </MobileDonorName>
                  {/* <MobileContactInfo>
                    {donation. && (
                      <MobileContactItem>{donation.donorEmail}</MobileContactItem>
                    )}
                    {donation.donorMobile && (
                      <MobileContactItem>{donation.donorMobile}</MobileContactItem>
                    )}
                  </MobileContactInfo> */}
                </MobileDonorColumn>
                <MobileRightColumn>
                  <MobileAmount>{formatCurrency(donation.amount)}</MobileAmount>
                  <MobileDateOnly>
                    {formatDateOnly(donation.donatedOn)}
                  </MobileDateOnly>
                  <MobileTimeOnly>
                    {formatTimeOnly(donation.donatedOn)}
                  </MobileTimeOnly>
                </MobileRightColumn>
              </MobileCardHeader>
            </MobileDonationCard>
          ))
        )}
      </MobileCardContainer>

      {/* Desktop Table Layout */}
      <DataTable>
        <TableHeader columns="2.5fr 1.2fr 2fr 2fr">
          <SortableHeader onClick={() => handleSort("name")}>
            <span>Name</span>
            {renderSortIcon("name")}
          </SortableHeader>
          <SortableHeader onClick={() => handleSort("donation")}>
            <span>Donation</span>
            {renderSortIcon("donation")}
          </SortableHeader>
          <SortableHeader onClick={() => handleSort("date")}>
            <span>Date</span>
            {renderSortIcon("date")}
          </SortableHeader>
          <div>Comment</div>
        </TableHeader>

        {sortedDonations.length === 0 ? (
          <NoResults>
            {searchTerm
              ? `No donations found matching "${searchTerm}"`
              : "No donations yet"}
          </NoResults>
        ) : (
          sortedDonations.map((donation, key) => (
            <TableRow key={key} columns="2.5fr 1.2fr 2fr 2fr">
              <DonorInfo>
                <DonorAvatar isAnonymous={donation.isAnonymous}>
                  {getInitials(getDisplayName(donation))}
                </DonorAvatar>
                <DonorDetails>
                  <DonorName isAnonymous={donation.isAnonymous}>
                    {getDisplayName(donation)}
                  </DonorName>
                  {!donation.isAnonymous &&
                    getContactInfo(donation).map((contact, index) => (
                      <ContactItem key={index}>{contact}</ContactItem>
                    ))}
                </DonorDetails>
              </DonorInfo>

              <AmountValue>{formatCurrency(donation.amount)}</AmountValue>

              <DateTimeValue>{formatDate(donation.donatedOn)}</DateTimeValue>

              {renderCommentCell(donation)}
            </TableRow>
          ))
        )}
      </DataTable>

      <DonationDetailModal
        isVisible={showDetailModal}
        donation={selectedDonation}
        onClose={handleCloseDetailModal}
      />

      <RefundModal
        isVisible={showRefundModal}
        donation={refundDonation}
        onClose={handleCloseRefundModal}
      />
    </>
  );
};
